import React from 'react';
import {
  getUserMenuTree,
  type MenuOpenMode,
  type UserMenuTreeItem,
} from '@/services/admin/menu';
import { isPublicAuthPath } from '@/utils/authSession';
import routesConfig from '../../config/routes';

export type MenuSource = 'backend' | 'routes';
/**
 * 菜单来源开关（调试时直接改这里）：
 * - 'backend'：从后端 menu/user-tree 加载
 * - 'routes'：从 config/routes 生成
 */
export const FIXED_MENU_SOURCE: MenuSource = 'backend';

/** 扁平化后的用户菜单项（权限校验、iframe 等） */
export interface UserMenuRawItem {
  menuId: string;
  name: string;
  code: string;
  path?: string;
  openMode?: MenuOpenMode;
  sortOrder?: number;
  parentId?: string;
  visible?: boolean;
}

/** 旧版按角色菜单项（兼容 RawMenuItem 联合类型） */
export interface MenuByRoleItem {
  uuid: string;
  showOrder: number;
  createBy: string;
  updateBy: string;
  createAt: string;
  updateAt: string;
  /** 菜单标识，对应 routes 中的 code */
  name: string;
  title: string;
  parentID: string;
  isFrame: boolean;
  path?: string;
}

/**
 * 菜单节点类型（构建后的菜单树结构）
 */
export interface MenuNode {
  path: string;
  menuId?: string;
  /** 侧栏唯一 key，优先 menuId */
  key?: string;
  name?: string; // 某些路由（如 redirect）可能没有 name
  icon?: React.ReactNode; // 可以是字符串（原始配置）或 ReactNode（处理后的图标组件）
  code?: string;
  routes?: MenuNode[];
  children?: MenuNode[];
  component?: string;
  redirect?: string;
  wrappers?: string[];
  layout?: boolean;
  openMode?: MenuOpenMode;
  externalUrl?: string;
  [key: string]: unknown; // 允许其他路由配置属性
}

export type RawMenuItem = UserMenuRawItem | MenuByRoleItem;

function buildFrameRoutePath(code: string) {
  return `/iframe/${encodeURIComponent(String(code || '').trim())}`;
}

function buildBlankMenuPath(code: string) {
  return `/external/${encodeURIComponent(String(code || '').trim())}`;
}

const MENU_GROUP_PATH_PREFIX = '/_menu/';

/** 侧栏父级分组占位 path，避免与 routes 前缀冲突导致多个父节点同时高亮 */
export function buildMenuGroupPath(menuId: string): string {
  return `${MENU_GROUP_PATH_PREFIX}${encodeURIComponent(
    String(menuId || '').trim(),
  )}`;
}

export function isMenuGroupPath(path?: string): boolean {
  return String(path || '').startsWith(MENU_GROUP_PATH_PREFIX);
}

export function parseBlankMenuCodeFromPath(pathname: string): string | null {
  const match = String(pathname || '').match(/^\/external\/([^/]+)$/);
  if (!match?.[1]) {
    return null;
  }
  try {
    return decodeURIComponent(match[1]).trim();
  } catch {
    return match[1].trim();
  }
}

export function openBlankMenuUrls(urls: string[]) {
  urls.forEach((url) => {
    const target = String(url || '').trim();
    if (target) {
      window.open(target, '_blank', 'noopener,noreferrer');
    }
  });
}

export type MenuNavigateResult = {
  path: string | null;
  blankUrls: string[];
};

function isBlankMenuNode(node: MenuNode): boolean {
  return (
    node.openMode === 'blank' && Boolean(String(node.externalUrl || '').trim())
  );
}

function getRoutablePathFromMenuNode(node: MenuNode): string | null {
  if (isBlankMenuNode(node)) {
    return null;
  }
  const path = getNavigablePathFromMenuNode(node);
  if (!path || path.startsWith('/external/')) {
    return null;
  }
  return path;
}

/** 按菜单顺序找首个可路由页面；遇到新窗口外链则收集 URL 并继续找站内/iframe 页面 */
function findMenuNavigateTargetInNodes(nodes: MenuNode[]): MenuNavigateResult {
  const blankUrls: string[] = [];

  const walk = (items: MenuNode[]): string | null => {
    for (const node of items) {
      const children = getMenuChildren(node);
      if (children?.length) {
        const nested = walk(children);
        if (nested) {
          return nested;
        }
      }

      if (isBlankMenuNode(node)) {
        blankUrls.push(String(node.externalUrl));
        continue;
      }

      const path = getRoutablePathFromMenuNode(node);
      if (path) {
        return path;
      }
    }
    return null;
  };

  return {
    path: walk(nodes),
    blankUrls,
  };
}

function isButtonCode(code: string) {
  const normalized = (code || '').trim();
  return Boolean(normalized && /(^|-)Button($|-)/i.test(normalized));
}

function shouldShowCodeInMenu(code: string) {
  const normalized = (code || '').trim();
  if (!normalized) return false;
  return !/(^|-)Tab($|-)|(^|-)Button($|-)/i.test(normalized);
}

function sortMenuTreeItems(items: UserMenuTreeItem[]) {
  return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/** 控制台默认入口：固定为 /，不随菜单变化，全员可访问 */
export const CONSOLE_DEFAULT_ENTRY_PATH = '/';

/** 无需菜单权限即可访问的路径（登录页、系统默认首页、404 等） */
export const SYSTEM_PUBLIC_PATHS = [
  '/login',
  CONSOLE_DEFAULT_ENTRY_PATH,
  '/404',
  '/in-site-message/inbox',
] as const;

export function isSystemPublicPath(pathname: string): boolean {
  if (!pathname) return false;
  if ((SYSTEM_PUBLIC_PATHS as readonly string[]).includes(pathname)) {
    return true;
  }
  return pathname.startsWith('/iframe/') || pathname.startsWith('/external/');
}

function appendRawMenuItem(
  item: UserMenuTreeItem,
  rawMenuData: UserMenuRawItem[],
) {
  const code = String(item.code || '').trim();
  rawMenuData.push({
    menuId: item.menuId,
    name: item.name,
    code,
    path: item.path,
    openMode: item.openMode,
    sortOrder: item.sortOrder,
    parentId: item.parentId,
    visible: item.visible,
  });
}

export function flattenUserMenuTree(
  tree: UserMenuTreeItem[],
): UserMenuRawItem[] {
  return buildMenuFromUserTree(tree).rawMenuData;
}

function resolveMenuPath(
  item: UserMenuTreeItem,
  routes: MenuNode[],
): Pick<
  MenuNode,
  | 'path'
  | 'component'
  | 'redirect'
  | 'wrappers'
  | 'layout'
  | 'icon'
  | 'openMode'
  | 'externalUrl'
> {
  const code = String(item.code || '').trim();
  const openMode = (item.openMode || 'internal') as MenuOpenMode;

  if (openMode === 'iframe') {
    return {
      path: buildFrameRoutePath(code),
      component: './Frame',
      openMode,
      externalUrl: item.path,
    };
  }

  if (openMode === 'blank') {
    return {
      path: buildBlankMenuPath(code),
      openMode,
      externalUrl: item.path,
    };
  }

  const route = findRouteByCode(routes, code);
  return {
    path: route?.path || '',
    icon: route?.icon,
    component: route?.component,
    wrappers: route?.wrappers,
    layout: route?.layout,
    openMode: 'internal',
  };
}

/** 单次遍历同时构建侧栏菜单树与权限校验用的扁平菜单 */
function buildMenuFromUserTree(
  tree: UserMenuTreeItem[],
  routes: MenuNode[] = routesConfig as unknown as MenuNode[],
): { menuData: MenuNode[]; rawMenuData: UserMenuRawItem[] } {
  const rawMenuData: UserMenuRawItem[] = [];

  const buildNode = (item: UserMenuTreeItem): MenuNode | null => {
    if (item.visible === false) {
      return null;
    }
    const code = String(item.code || '').trim();
    // Button/Tab 不进侧栏，但仍写入 rawMenuData 供路由权限校验
    appendRawMenuItem(item, rawMenuData);
    if (!shouldShowCodeInMenu(code)) {
      sortMenuTreeItems(item.children || []).forEach((child) => {
        buildNode(child);
      });
      return null;
    }

    const resolved = resolveMenuPath(item, routes);
    const childNodes = sortMenuTreeItems(item.children || [])
      .map((child) => buildNode(child))
      .filter((node): node is MenuNode => Boolean(node));

    const menuNode: MenuNode = {
      menuId: item.menuId,
      key: item.menuId,
      name: item.name,
      code,
      ...resolved,
    };

    if (childNodes.length > 0) {
      menuNode.routes = childNodes;
      menuNode.path = buildMenuGroupPath(item.menuId);
    }

    if (!menuNode.path && !menuNode.routes?.length) {
      return null;
    }

    return menuNode;
  };

  const menuData = sortMenuTreeItems(tree)
    .map((item) => buildNode(item))
    .filter((node): node is MenuNode => Boolean(node));

  return { menuData, rawMenuData };
}

/**
 * 根据 user-tree 与 routes 配置构建侧栏菜单
 */
export function buildMenuTreeFromUserTree(
  tree: UserMenuTreeItem[],
  routes: MenuNode[] = routesConfig as unknown as MenuNode[],
): MenuNode[] {
  return buildMenuFromUserTree(tree, routes).menuData;
}

let userMenuTreeInflight: Promise<{
  menuData: MenuNode[];
  rawMenuData: UserMenuRawItem[];
}> | null = null;

export async function fetchMenuDataByUserTree(): Promise<{
  menuData: MenuNode[];
  rawMenuData: UserMenuRawItem[];
}> {
  if (userMenuTreeInflight) {
    return userMenuTreeInflight;
  }

  userMenuTreeInflight = (async () => {
    try {
      const response = await getUserMenuTree({ skipErrorHandler: true });
      if (!response || !Array.isArray(response.tree)) {
        throw new Error('用户菜单树响应格式异常');
      }
      return buildMenuFromUserTree(response.tree);
    } catch (error) {
      console.error('获取用户菜单树失败:', error);
      return {
        menuData: [],
        rawMenuData: [],
      };
    } finally {
      userMenuTreeInflight = null;
    }
  })();

  return userMenuTreeInflight;
}

export function getRawMenuCode(menu: RawMenuItem): string {
  if ('code' in menu && menu.code) {
    return String(menu.code).trim();
  }
  return String((menu as MenuByRoleItem).name || '').trim();
}

export function getRawMenuName(menu: RawMenuItem): string {
  if ('title' in menu && menu.title) {
    return String(menu.title).trim();
  }
  if ('name' in menu && menu.name) {
    return String(menu.name).trim();
  }
  return getRawMenuCode(menu);
}

/**
 * 按 pathname 解析页面标题：优先后端菜单名（含 hideInMenu 的 Button），
 * 再回退到 routes 配置的 name。供 PageContainer 在侧栏匹配失败时使用。
 */
export function resolvePageTitleByPathname(
  pathname: string,
  rawMenuData?: RawMenuItem[],
): string | undefined {
  const chain = getRouteChainCached(pathname);
  const route = chain?.[chain.length - 1];
  if (!route) return undefined;

  const code = String(route.code || '').trim();
  if (code && rawMenuData?.length) {
    const matched = rawMenuData.find((menu) => getRawMenuCode(menu) === code);
    const name = matched ? getRawMenuName(matched) : '';
    if (name) return name;
  }

  const routeName = String(route.name || '').trim();
  return routeName || undefined;
}

export function getRawMenuOpenMode(
  menu: RawMenuItem,
): MenuOpenMode | undefined {
  if ('openMode' in menu && menu.openMode) {
    return menu.openMode;
  }
  if ((menu as MenuByRoleItem).isFrame) {
    return 'iframe';
  }
  return 'internal';
}

export function getRawMenuExternalPath(
  menu: RawMenuItem | undefined,
): string | undefined {
  if (!menu) {
    return undefined;
  }
  if ('path' in menu && menu.path) {
    return menu.path;
  }
  return (menu as MenuByRoleItem).path;
}

let routeByCodeCache: Map<string, MenuNode> | null = null;

function buildRouteByCodeMap(routes: MenuNode[]): Map<string, MenuNode> {
  const map = new Map<string, MenuNode>();
  const walk = (items: MenuNode[]) => {
    items.forEach((route) => {
      const code = String(route.code || '').trim();
      if (code) {
        map.set(code, route);
      }
      if (route.routes?.length) {
        walk(route.routes);
      }
    });
  };
  walk(routes);
  return map;
}

function getRouteByCodeMap(
  routes: MenuNode[] = routesConfig as unknown as MenuNode[],
): Map<string, MenuNode> {
  if (!routeByCodeCache) {
    routeByCodeCache = buildRouteByCodeMap(routes);
  }
  return routeByCodeCache;
}

/** 递归查找路由配置中匹配 code 的路由 */
export function findRouteByCode(
  routes: MenuNode[],
  code: string,
): MenuNode | null {
  const normalized = String(code || '').trim();
  if (!normalized) return null;
  return getRouteByCodeMap(routes).get(normalized) ?? null;
}

export function getMenuSource(): MenuSource {
  return FIXED_MENU_SOURCE;
}

function shouldIncludeInSidebar(route: MenuNode): boolean {
  if (route.hideInMenu) return false;
  if (route.layout === false) return false;
  if (!route.path || route.path === '*') return false;
  if (isPublicAuthPath(route.path)) return false;
  if (!route.name) return false;
  if (route.path === '/' && route.redirect && !route.routes?.length) {
    return false;
  }
  return true;
}

/** 侧栏 key：user-tree 用 menuId；静态 routes 补全子项用 code */
function getMenuItemKey(item: Pick<MenuNode, 'key' | 'menuId' | 'code'>) {
  return item.key || item.menuId || item.code;
}

/** 仅保留侧栏菜单渲染所需字段，避免 redirect/component 等干扰 ProLayout 跳转 */
function toSidebarMenuNode(route: MenuNode): MenuNode {
  const node: MenuNode = {
    path: route.path,
    menuId: route.menuId,
    key: getMenuItemKey(route),
    name: route.name,
    code: route.code,
    icon: route.icon,
    openMode: route.openMode,
    externalUrl: route.externalUrl,
  };
  if (route.routes?.length) {
    node.routes = route.routes;
  }
  return node;
}

export function buildMenuFromRoutes(
  routes: MenuNode[] = routesConfig as unknown as MenuNode[],
): MenuNode[] {
  const walk = (items: MenuNode[]): MenuNode[] => {
    const out: MenuNode[] = [];
    for (const item of items) {
      if (!shouldIncludeInSidebar(item)) continue;
      const child = item.routes?.length ? walk(item.routes) : [];
      const next = toSidebarMenuNode(item);
      if (child.length > 0) {
        next.routes = child;
      }
      out.push(next);
    }
    return out;
  };

  return walk(routes);
}

export function getStaticMenuData(): MenuNode[] {
  return buildMenuFromRoutes();
}

/** ProLayout 侧栏使用 children 字段，将 routes 转为 children */
export function normalizeMenuForProLayout(
  items: MenuNode[],
  parentKeys: string[] = [],
): MenuNode[] {
  return items.map((item) => {
    const menuKey = String(item.menuId || item.key || '').trim();
    const childSource = item.routes?.length ? item.routes : undefined;
    const normalized: MenuNode = {
      path: item.path,
      menuId: item.menuId,
      key: menuKey || item.key,
      name: item.name,
      code: item.code,
      icon: item.icon,
      openMode: item.openMode,
      externalUrl: item.externalUrl,
    };
    if (parentKeys.length) {
      normalized.pro_layout_parentKeys = parentKeys;
    }
    if (childSource?.length) {
      const nextParentKeys = menuKey ? [...parentKeys, menuKey] : parentKeys;
      normalized.children = normalizeMenuForProLayout(
        childSource,
        nextParentKeys,
      );
    }
    return normalized;
  });
}

function matchesDynamicPath(pattern: string, pathname: string): boolean {
  const a = pattern.split('/').filter(Boolean);
  const b = pathname.split('/').filter(Boolean);
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].startsWith(':')) continue;
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function canAccessPathByRoutes(
  pathname: string,
  routes: MenuNode[] = routesConfig as unknown as MenuNode[],
): boolean {
  if (!pathname) return false;
  for (const route of routes) {
    if (!route.path || route.path === '*') continue;
    if (route.layout === false && isPublicAuthPath(route.path)) continue;
    if (
      matchesDynamicPath(route.path, pathname) &&
      (route.component || route.redirect || route.routes?.length)
    ) {
      return true;
    }
    if (route.routes?.length && canAccessPathByRoutes(pathname, route.routes)) {
      return true;
    }
  }
  return false;
}

export interface MenuRuntimeState {
  menuData: MenuNode[];
  rawMenuData: RawMenuItem[];
  menuDataLoaded: boolean;
  menuSource: MenuSource;
}

/** 与 @@initialState 中菜单相关字段对齐（均为可选，登录前/加载中可能未赋值） */
export type MenuAccessInitialState = Partial<
  Pick<MenuRuntimeState, 'menuDataLoaded' | 'menuSource'>
> & {
  currentUser?: unknown;
  menuLoading?: boolean;
};

/** 已登录且后端菜单尚未加载完成（此期间应展示 loading，不做 404 跳转） */
export function isMenuAccessPending(state?: MenuAccessInitialState): boolean {
  if (!state?.currentUser) {
    return false;
  }
  if (state.menuSource === 'routes') {
    return false;
  }
  if (state.menuLoading) {
    return true;
  }
  return !state.menuDataLoaded;
}

function buildMenuRuntimeState(
  result: { menuData: MenuNode[]; rawMenuData: UserMenuRawItem[] },
  source: MenuSource,
): MenuRuntimeState {
  syncMenuAccessCache(result.rawMenuData);
  return {
    menuData: result.menuData,
    rawMenuData: result.rawMenuData,
    menuDataLoaded: true,
    menuSource: source,
  };
}

export function getMenuChildren(menu: MenuNode): MenuNode[] | undefined {
  return menu.children?.length ? menu.children : menu.routes;
}

function getNavigablePathFromMenuNode(node: MenuNode): string | null {
  const path = String(node.path || '').trim();
  if (
    path &&
    !isMenuGroupPath(path) &&
    path !== '/login' &&
    path !== '*' &&
    path !== '/'
  ) {
    return path;
  }
  const redirect = String(node.redirect || '').trim();
  if (redirect && redirect !== '/login' && redirect !== '/') {
    return redirect;
  }
  return null;
}

/**
 * 侧栏首个可导航菜单路径（用于父级菜单组点击等场景，非控制台默认首页）
 */
export function findDefaultHomePath(menus: MenuNode[]): string | null {
  if (!menus.length) {
    return null;
  }
  for (const menu of menus) {
    const path = findMenuNavigateTargetInNodes([menu]).path;
    if (path) {
      return path;
    }
  }
  return null;
}

export function findDefaultMenuNavigateTarget(
  menus: MenuNode[],
): MenuNavigateResult {
  if (!menus.length) {
    return { path: null, blankUrls: [] };
  }
  for (const menu of menus) {
    const result = findMenuNavigateTargetInNodes([menu]);
    if (result.path || result.blankUrls.length) {
      return result;
    }
  }
  return { path: null, blankUrls: [] };
}

/** @deprecated 请使用 findDefaultHomePath；保留别名兼容旧引用 */
export function findFirstAccessiblePath(menus: MenuNode[]): string | null {
  return findDefaultHomePath(menus);
}

/** 在指定菜单节点下查找首个可导航路径（用于父级菜单点击跳转） */
export function findDefaultHomePathUnderMenu(node: MenuNode): string | null {
  return findMenuNavigateTargetInNodes([node]).path;
}

export function findMenuNavigateTargetUnderMenu(
  node: MenuNode,
): MenuNavigateResult {
  return findMenuNavigateTargetInNodes([node]);
}

export function findMenuNodeByExactPath(
  menus: MenuNode[],
  pathname: string,
): MenuNode | null {
  for (const menu of menus) {
    if (menu.path === pathname) {
      return menu;
    }
    const children = getMenuChildren(menu);
    if (children?.length) {
      const found = findMenuNodeByExactPath(children, pathname);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export function findMenuNodeByCode(
  menus: MenuNode[],
  code: string,
): MenuNode | null {
  const normalized = String(code || '').trim();
  if (!normalized) {
    return null;
  }
  for (const menu of menus) {
    if (String(menu.code || '').trim() === normalized) {
      return menu;
    }
    const children = getMenuChildren(menu);
    if (children?.length) {
      const found = findMenuNodeByCode(children, normalized);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export function findMenuNodeByMenuId(
  menus: MenuNode[],
  menuId: string,
): MenuNode | null {
  const normalized = String(menuId || '').trim();
  if (!normalized) {
    return null;
  }
  for (const menu of menus) {
    if (String(menu.menuId || menu.key || '').trim() === normalized) {
      return menu;
    }
    const children = getMenuChildren(menu);
    if (children?.length) {
      const found = findMenuNodeByMenuId(children, normalized);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

function findRouteGroupByPath(
  pathname: string,
  routes: MenuNode[],
): MenuNode | null {
  for (const route of routes) {
    if (route.path === pathname && route.routes?.length) {
      return route;
    }
    if (route.routes?.length) {
      const found = findRouteGroupByPath(pathname, route.routes);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

function isMenuRedirectRoute(route: MenuNode): boolean {
  return String(route.component || '').trim() === './MenuRedirect';
}

/** routes 模式下兜底：取分组下首个可见页面路由 */
function findFirstNavigableRoutePath(group: MenuNode): string | null {
  for (const route of group.routes || []) {
    if (route.hideInMenu || isMenuRedirectRoute(route)) {
      continue;
    }
    if (route.component && route.path) {
      return route.path;
    }
    if (route.routes?.length) {
      const nested = findFirstNavigableRoutePath(route);
      if (nested) {
        return nested;
      }
    }
  }
  return null;
}

/**
 * 分组菜单默认跳转：一级 → 首个二级，二级 → 首个三级（依菜单树顺序，尊重权限过滤后的菜单）
 */
export function resolveMenuGroupRedirectTarget(
  pathname: string,
  menuData: MenuNode[],
  routes: MenuNode[] = routesConfig as unknown as MenuNode[],
  menuSource: MenuSource = getMenuSource(),
): MenuNavigateResult {
  const normalizedPath = String(pathname || '').trim();
  const empty: MenuNavigateResult = { path: null, blankUrls: [] };
  if (!normalizedPath) {
    return empty;
  }

  const pickFromNode = (node: MenuNode | null): MenuNavigateResult | null => {
    if (!node || !getMenuChildren(node)?.length) {
      return null;
    }
    const result = findMenuNavigateTargetInNodes([node]);
    if (result.path && result.path !== normalizedPath) {
      return result;
    }
    if (!result.path && result.blankUrls.length) {
      return result;
    }
    return null;
  };

  let result = pickFromNode(findMenuNodeByExactPath(menuData, normalizedPath));
  if (result) {
    return result;
  }

  const routeGroup = findRouteGroupByPath(normalizedPath, routes);
  if (routeGroup?.code) {
    result = pickFromNode(findMenuNodeByCode(menuData, routeGroup.code));
    if (result) {
      return result;
    }
  }

  if (routeGroup) {
    // 后端模式不能从静态配置推导权限；仅显式 routes 调试模式保留此回退。
    if (menuSource !== 'routes') return empty;
    const fallback = findFirstNavigableRoutePath(routeGroup);
    if (fallback && fallback !== normalizedPath) {
      return { path: fallback, blankUrls: [] };
    }
  }

  const home = findDefaultMenuNavigateTarget(menuData);
  if (home.path && home.path !== normalizedPath) {
    return home;
  }
  if (home.blankUrls.length) {
    return home;
  }

  return empty;
}

/** @deprecated 请使用 resolveMenuGroupRedirectTarget */
export function resolveMenuGroupRedirectPath(
  pathname: string,
  menuData: MenuNode[],
  routes: MenuNode[] = routesConfig as unknown as MenuNode[],
): string | null {
  return resolveMenuGroupRedirectTarget(pathname, menuData, routes).path;
}

export function applyMenuNavigateResult(
  result: MenuNavigateResult,
  pathname: string,
  menuData: MenuNode[],
  replace: (path: string) => void,
): boolean {
  if (result.blankUrls.length) {
    openBlankMenuUrls(result.blankUrls);
  }

  if (result.path && result.path !== pathname) {
    replace(result.path);
    return true;
  }

  if (result.blankUrls.length) {
    const fallback = findDefaultHomePath(menuData);
    if (fallback && fallback !== pathname) {
      replace(fallback);
    }
    return true;
  }

  return false;
}

/** 无可用菜单时的兜底跳转；控制台默认首页固定为 `/`（Home），见 CONSOLE_DEFAULT_ENTRY_PATH */
export function applyDefaultMenuNavigateOr404(
  menuData: MenuNode[],
  pathname: string,
  replace: (path: string) => void,
): void {
  const result = findDefaultMenuNavigateTarget(menuData);
  if (applyMenuNavigateResult(result, pathname, menuData, replace)) {
    return;
  }
  replace('/404');
}

type MenuAccessCache = {
  rawRef: RawMenuItem[];
  allowedCodes: Set<string>;
  specialPaths: Set<string>;
  routeChainCache: Map<string, MenuNode[] | null>;
};

let menuAccessCache: MenuAccessCache | null = null;

function syncMenuAccessCache(rawMenuData: RawMenuItem[]) {
  if (menuAccessCache?.rawRef === rawMenuData) {
    return;
  }

  const allowedCodes = new Set(
    rawMenuData.map((menu) => getRawMenuCode(menu)).filter(Boolean),
  );
  const specialPaths = new Set<string>();

  for (const menu of rawMenuData) {
    const code = getRawMenuCode(menu);
    const openMode = getRawMenuOpenMode(menu);
    if (openMode === 'iframe') {
      specialPaths.add(buildFrameRoutePath(code));
    }
    if (openMode === 'blank') {
      specialPaths.add(buildBlankMenuPath(code));
    }
  }

  menuAccessCache = {
    rawRef: rawMenuData,
    allowedCodes,
    specialPaths,
    routeChainCache: new Map(),
  };
}

export async function fetchMenuDataBySource(params: {
  currentUser?: USERS.AuthUserInfo;
  source?: MenuSource;
  allowTokenOnly?: boolean;
}): Promise<MenuRuntimeState> {
  const source = params.source || getMenuSource();
  const currentUser = params.currentUser;

  if (!currentUser && !params.allowTokenOnly) {
    return {
      menuData: [],
      rawMenuData: [],
      menuDataLoaded: false,
      menuSource: source,
    };
  }

  if (source === 'routes') {
    const menuData = getStaticMenuData();
    syncMenuAccessCache([]);
    return {
      menuData,
      rawMenuData: [],
      menuDataLoaded: true,
      menuSource: source,
    };
  }

  const result = await fetchMenuDataByUserTree();
  return buildMenuRuntimeState(result, source);
}

function getDirectParentCodeFromChain(chain: MenuNode[]): string | null {
  for (let i = chain.length - 2; i >= 0; i -= 1) {
    const code = String(chain[i].code || '').trim();
    if (code) return code;
  }
  return null;
}

/** 在 routes 中定位与 pathname 匹配的路由链（含父级分组节点） */
function findRouteChainByPathname(
  pathname: string,
  routes: MenuNode[],
  parents: MenuNode[] = [],
): MenuNode[] | null {
  for (const route of routes) {
    const chain = [...parents, route];
    if (route.routes?.length) {
      const found = findRouteChainByPathname(pathname, route.routes, chain);
      if (found) return found;
    }
    if (
      route.path &&
      route.path !== '*' &&
      matchesDynamicPath(route.path, pathname) &&
      (route.component || route.redirect)
    ) {
      return chain;
    }
  }
  return null;
}

function getRouteChainCached(pathname: string): MenuNode[] | null {
  if (!menuAccessCache) {
    return findRouteChainByPathname(
      pathname,
      routesConfig as unknown as MenuNode[],
    );
  }

  const cached = menuAccessCache.routeChainCache.get(pathname);
  if (cached !== undefined) {
    return cached;
  }

  const chain = findRouteChainByPathname(
    pathname,
    routesConfig as unknown as MenuNode[],
  );
  menuAccessCache.routeChainCache.set(pathname, chain);
  return chain;
}

export function canAccessPathByRawMenuData(
  pathname: string,
  rawMenuData: RawMenuItem[],
): boolean {
  if (!pathname || !rawMenuData?.length) return false;

  syncMenuAccessCache(rawMenuData);
  if (menuAccessCache?.specialPaths.has(pathname)) {
    return true;
  }

  const allowedCodes = menuAccessCache?.allowedCodes ?? new Set<string>();
  const chain = getRouteChainCached(pathname);
  if (!chain?.length) return false;

  const target = chain[chain.length - 1];
  const targetCode = String(target.code || '').trim();
  if (targetCode && allowedCodes.has(targetCode)) {
    return true;
  }

  // Button 路由必须显式授权，不回退父级列表权限
  if (isButtonCode(targetCode)) {
    return false;
  }

  const directParentCode = getDirectParentCodeFromChain(chain);

  // hideInMenu / 无 code 的 redirect：仅当直接父级菜单 code 已授权
  if (target.hideInMenu || (target.redirect && !targetCode)) {
    return directParentCode ? allowedCodes.has(directParentCode) : false;
  }

  return false;
}

export function canAccessPathByMenuState(
  pathname: string,
  menuState: MenuRuntimeState,
): boolean {
  if (isSystemPublicPath(pathname)) {
    return true;
  }
  if (menuState.menuSource === 'routes') {
    return canAccessPathByRoutes(pathname);
  }
  if (!menuState.menuDataLoaded) {
    return false;
  }
  return canAccessPathByRawMenuData(pathname, menuState.rawMenuData);
}

/**
 * 递归查找菜单树中某个 path 的路径链（父级链路）
 */
export function findMenuPathByPath(
  menus: MenuNode[],
  targetPath: string,
  parents: MenuNode[] = [],
): MenuNode[] | null {
  for (const node of menus) {
    const chain = [...parents, node];
    const path = String(node.path || '').trim();
    if (
      path &&
      !isMenuGroupPath(path) &&
      (path === targetPath || matchesDynamicPath(path, targetPath))
    ) {
      return chain;
    }
    const children = getMenuChildren(node);
    if (children?.length) {
      const found = findMenuPathByPath(children, targetPath, chain);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/** 基于 user-tree 菜单结构计算侧栏选中/展开项（使用 menuId 作为 key） */
export function getMenuSelectionState(
  pathname: string,
  menus: MenuNode[],
): { selectedKeys: string[]; openKeys: string[] } {
  const chain = findMenuPathByPath(menus, pathname);
  if (!chain?.length) {
    return { selectedKeys: [], openKeys: [] };
  }

  const keys = chain
    .map((node) => String(node.menuId || node.key || '').trim())
    .filter(Boolean);
  if (!keys.length) {
    return { selectedKeys: [], openKeys: [] };
  }

  return {
    selectedKeys: [keys[keys.length - 1]],
    openKeys: keys.slice(0, -1),
  };
}
